<script setup>
import { i18n } from 'boot/i18n';
import Banner from 'components/UI/Banner.vue';
import { useDialogPluginComponent } from 'quasar';
import { computed, ref } from 'vue';

import { DEFAULT_FOCUS_AREAS, DEFAULT_STRICTNESS_MODE } from '../../domain/constants/aiAuditorSettings';
import { isSessionActivityAuditable } from '../../domain/constants/auditableSessionActivityStatuses';
import { hasCurrentStoredAudit } from '../../domain/services/auditFreshness';
import { useAuditRunner } from '../composables/useAuditRunner';
import AuditResultsTable from './AuditResultsTable.vue';
import AuditSettingsForm from './AuditSettingsForm.vue';

defineOptions({ name: 'NoteAuditDialog' });

const props = defineProps({
  sessionActivities: {
    type: Array,
    required: true,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const {
  dialogRef,
  onDialogHide,
  onDialogOK,
  onDialogCancel,
} = useDialogPluginComponent();

const STEPS = {
  CONFIGURATION: 'configuration',
  RESULTS: 'results',
};

const {
  auditTasks,
  isRunning,
  prepare,
  run,
  cancel,
} = useAuditRunner();

const step = ref(STEPS.CONFIGURATION);

const settings = ref({
  focusAreas: DEFAULT_FOCUS_AREAS,
  strictness: DEFAULT_STRICTNESS_MODE,
});

const selectedActivities = ref([...props.sessionActivities]);

const eligibleActivities = computed(() => (
  selectedActivities.value.filter((activity) => isSessionActivityAuditable(activity))
));

const numSessionActivities = computed(() => eligibleActivities.value.length);

const nonSessionActivitiesSelected = computed(() => (
  selectedActivities.value.some((activity) => !isSessionActivityAuditable(activity))
));

// Dismissing a result clears the verdict but leaves the timestamp, and such a note has nothing to re-read.
const auditedUnchangedActivities = computed(() => (
  eligibleActivities.value.filter((activity) => hasCurrentStoredAudit(activity))
));

const bannerConfigs = computed(() => {
  const banners = [];

  if (nonSessionActivitiesSelected.value) {
    banners.push({
      type: 'info',
      icon: 'info',
      message: i18n.t('noteAuditor.nonSessionActivitiesNotice', {
        eligibleCount: eligibleActivities.value.length,
        totalCount: selectedActivities.value.length,
      }),
      showAction: false,
    });
  }

  if (auditedUnchangedActivities.value.length > 0) {
    banners.push({
      type: 'warning',
      icon: 'warning',
      message: i18n.t('noteAuditor.alreadyAuditedNotice', {
        auditedCount: auditedUnchangedActivities.value.length,
        eligibleCount: eligibleActivities.value.length,
      }),
      showAction: true,
    });
  }

  return banners;
});

const areBannersVisible = computed(() => (
  step.value === STEPS.CONFIGURATION && bannerConfigs.value.length > 0
));

/**
 * Start the audit process with the current settings. This will prepare the
 * audit tasks based on the provided session activities and then execute them,
 * updating the UI with the results.
 * @returns {Promise<void>}
 */
function startAudit() {
  if (eligibleActivities.value.length === 0) {
    return Promise.resolve();
  }

  prepare(eligibleActivities.value);
  step.value = STEPS.RESULTS;
  return run(settings.value);
}

/**
 * Cancel the ongoing audit process. If the audit is currently running, this will signal the audit runner to stop
 * processing any further tasks and will close the dialog.
 */
function closeDialog() {
  if (step.value === STEPS.CONFIGURATION) {
    onDialogHide();
    return;
  }
  if (isRunning.value) {
    cancel();
  }
  onDialogOK();
} // closeDialog()

function deselectAuditedActivities() {
  const auditedIds = new Set(
    auditedUnchangedActivities.value.map(({ id }) => id),
  );

  selectedActivities.value = selectedActivities.value.filter(
    ({ id }) => !auditedIds.has(id),
  );
}

</script>
<template>
  <q-dialog
    ref="dialogRef"
    :maximized="$q.platform.is.mobile"
    persistent
    @hide="onDialogHide"
  >
    <q-card class="note-audit-dialog q-pa-lg">
      <q-card-section class="row items-start q-pa-none">
        <div class="col">
          <div class="text-h6">
            {{ i18n.t('noteAuditor.dialogTitle') }}
          </div>
          <div class="text-body2 q-mt-xs">
            {{ i18n.t('noteAuditor.aiAuditAgentDescription') }}
          </div>
        </div>
        <div class="col-auto">
          <q-btn
            flat
            round
            dense
            icon="close"
            @click="closeDialog"
          />
        </div>
      </q-card-section>
      <q-card-section
        v-if="step === STEPS.CONFIGURATION && bannerConfigs.length"
        class="q-py-lg q-px-none q-mx-n-lg"
      >
        <banner
          v-for="(bannerConfig, index) in bannerConfigs"
          :key="bannerConfig.message"
          :class="{ 'q-mb-lg': index < bannerConfigs.length - 1 }"
          :type="bannerConfig.type"
          :icon="bannerConfig.icon"
          :message="bannerConfig.message"
        >
          <template
            v-if="bannerConfig.showAction"
            #action
          >
            <q-btn
              flat
              color="primary"
              :label="i18n.t('noteAuditor.deselectAudited')"
              @click="deselectAuditedActivities"
            />
          </template>
        </banner>
      </q-card-section>
      <q-card-section
        class="q-pa-none"
        :class="areBannersVisible ? 'q-mt-none' : 'q-mt-lg'"
      >
        <audit-settings-form
          v-if="step === STEPS.CONFIGURATION"
          v-model:settings="settings"
        />

        <audit-results-table
          v-else-if="step === STEPS.RESULTS"
          :audit-tasks="auditTasks"
          :is-running="isRunning"
        />
      </q-card-section>
      <q-card-actions
        class="row items-center justify-between q-pa-none q-mt-lg"
      >
        <div class="text-caption text-grey-7">
          {{ i18n.t('noteAuditor.aiAccuracyDisclaimer') }}
        </div>

        <div class="row q-gutter-sm">
          <template v-if="step === STEPS.CONFIGURATION">
            <q-btn
              flat
              :label="i18n.t('common.buttonCancel')"
              color="primary"
              @click="onDialogCancel"
            />

            <q-btn
              flat
              :label="i18n.tc(
                'noteAuditor.buttonRunAudit',
                numSessionActivities,
                { count: numSessionActivities },
              )"
              :disable="numSessionActivities === 0"
              color="primary"
              @click="startAudit"
            />
          </template>

          <template v-else-if="step === STEPS.RESULTS">
            <q-btn
              flat
              :label="isRunning ? i18n.t('common.buttonCancel') : i18n.t('common.buttonClose')"
              color="primary"
              @click="closeDialog"
            />
          </template>
        </div>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<style scoped>
.note-audit-dialog {
  min-width: 60vw;
}
</style>
